import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { UserSummary } from '../api/types';
import { Avatar } from '../components/Avatar';
import { StatusPill } from '../components/StatusPill';
import { TagInput } from '../components/TagInput';
import { UserFormModal } from '../components/UserFormModal';
import { can } from '../lib/roles';
import { getRuntimeConfig } from '../runtimeConfig';
import { useApp } from '../state/AppContext';
import { useToast } from '../state/ToastContext';
import { relativeTime } from '../utils/format';
import { photoSrc } from '../utils/photo';
import { distinctTags, hasAnyTag, tagsOf } from '../utils/tags';
import { createdToast, matchesQuery, revealFilters } from '../utils/users';

const FILTERS = ['All', 'Active', 'Pending', 'Suspended'];
const TAGS_SHOWN = 3;
const HIGHLIGHT_MS = 2600;

export function UsersPage() {
  const { me, users, systems, systemById, loading, error, refresh, upsertUser } = useApp();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { apiUrl } = getRuntimeConfig();
  const canCreate = can(me.clientRole, 'manageUsers');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('Active');
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const highlightRef = useRef<HTMLDivElement>(null);
  const allTags = useMemo(() => distinctTags(users), [users]);

  useEffect(() => {
    if (!highlightId) return;
    highlightRef.current?.scrollIntoView({ block: 'nearest' });
    const timer = setTimeout(() => setHighlightId(null), HIGHLIGHT_MS);
    return () => clearTimeout(timer);
  }, [highlightId]);

  const query = search.trim().toLowerCase();
  const filtered = users.filter(
    (user) =>
      (filter === 'All' || user.status === filter) &&
      hasAnyTag(tagsOf(user), tagFilter) &&
      matchesQuery(user, query),
  );

  function created(message: string, user?: UserSummary) {
    setCreating(false);
    if (!user) {
      showToast(message);
      void refresh();
      return;
    }
    upsertUser(user);
    const next = revealFilters(user, { filter, search, tagFilter }, FILTERS);
    setFilter(next.filter);
    setSearch(next.search);
    setTagFilter(next.tagFilter);
    setHighlightId(user.id);
    showToast(createdToast(message, user));
    void refresh();
  }

  return (
    <div className="page page-full">
      <div className="page-header">
        <div>
          <h1>Users</h1>
          <div className="page-sub">
            One place to manage identity and access across every connected system.
          </div>
        </div>
        <div className="page-header-actions">
          <div className="page-meta">
            {users.length} people · {systems.length} systems
          </div>
          {canCreate && (
            <button className="btn-primary" onClick={() => setCreating(true)}>
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
              New user
            </button>
          )}
        </div>
      </div>

      <div className="toolbar">
        <div className="search-wrap">
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none" className="search-icon">
            <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5" />
            <path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <input
            className="search-input"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search name, email or username…"
          />
        </div>
        <div className="tag-filter">
          <TagInput
            value={tagFilter}
            onChange={setTagFilter}
            suggestions={allTags}
            allowCreate={false}
            placeholder="Filter by tags…"
          />
        </div>
        <div className="chip-row">
          {FILTERS.map((label) => {
            const count =
              label === 'All'
                ? users.length
                : users.filter((user) => user.status === label).length;
            return (
              <div
                key={label}
                className={`chip${filter === label ? ' active' : ''}`}
                onClick={() => setFilter(label)}
              >
                {label} <span className="chip-count">{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="card table-card">
        <div className="table-scroll users-scroll">
          <div className="users-grid users-grid-head">
            <div>User</div>
            <div>Username</div>
            <div>Department</div>
            <div>Tags</div>
            <div>Systems</div>
            <div title="Keycloak account status">Keycloak</div>
            <div>Last active</div>
          </div>
          {loading && <div className="empty-state">Loading users…</div>}
          {!loading &&
            filtered.map((user) => {
              const dots = user.grants.slice(0, 4);
              const more = user.grants.length - dots.length;
              const tags = tagsOf(user);
              const shownTags = tags.slice(0, TAGS_SHOWN);
              const moreTags = tags.length - shownTags.length;
              const isNew = user.id === highlightId;
              return (
                <div
                  key={user.id}
                  ref={isNew ? highlightRef : undefined}
                  className={`users-grid users-grid-row${isNew ? ' is-new' : ''}`}
                  onClick={() => navigate(`/users/${user.id}`)}
                >
                  <div className="user-cell">
                    <Avatar
                      name={user.name}
                      photoSrc={photoSrc(apiUrl, user.id, user.photoVersion)}
                    />
                    <div className="user-cell-meta">
                      <div className="user-cell-name">{user.name}</div>
                      <div className="user-cell-email">{user.email ?? '—'}</div>
                    </div>
                  </div>
                  <div className="mono muted">{user.username}</div>
                  <div className="dept">{user.department ?? '—'}</div>
                  <div className="tag-cell" title={tags.join(', ')}>
                    {tags.length === 0 && <span className="dept">—</span>}
                    {shownTags.map((tag) => (
                      <span key={tag} className="tag-pill">
                        {tag}
                      </span>
                    ))}
                    {moreTags > 0 && <span className="sys-more">+{moreTags}</span>}
                  </div>
                  <div className="sys-dots">
                    {dots.map((grant) => {
                      const system = systemById(grant.systemId);
                      return (
                        <div
                          key={`${grant.systemId}-${grant.role}`}
                          className="sys-dot"
                          title={system ? `${system.name} · ${grant.role}` : grant.systemId}
                          style={{ background: system?.color ?? '#8a918d' }}
                        >
                          {system?.abbr ?? '?'}
                        </div>
                      );
                    })}
                    {more > 0 && <span className="sys-more">+{more}</span>}
                  </div>
                  <div>
                    <StatusPill status={user.status} />
                  </div>
                  <div className="mono muted">{relativeTime(user.lastActive)}</div>
                </div>
              );
            })}
          {!loading && filtered.length === 0 && (
            <div className="empty-state">
              {query && tagFilter.length === 0
                ? `No users match "${search}"`
                : 'No users match the current filters'}
            </div>
          )}
        </div>
      </div>

      {creating && (
        <UserFormModal
          mode={{ kind: 'create' }}
          onClose={() => setCreating(false)}
          onSaved={created}
        />
      )}
    </div>
  );
}
